import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { SiteNavigation } from "../components/navigation/SiteNavigation";
import { LibraryNavigation } from "../components/navigation/LibraryNavigation";
import { toast } from "sonner";
import { Ticket, Download, Music4, Film, Package, Crown, Coins, Play, Loader2, ShoppingBag, ArrowLeft } from "lucide-react";
import { useAuth } from "../App";
import TeaserModal from "../components/TeaserModal";
import AddressConfirmationCard from "../components/AddressConfirmationCard";
import WalletHoldBanner from "../components/WalletHoldBanner";
import TipButton from "../components/TipButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fan Wallet — one home for every song, movie, ticket, subscription, tip and
// challenge entry a fan has ever paid for on StardomFans. The "own your library"
// view a subscription platform is missing without.
const Wallet = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState(null);
  const [teaser, setTeaser] = useState({ open: false, item: null, purchaseState: null });

  const fetchWallet = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/fan/wallet`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWallet(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load your wallet");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchWallet(); }, [token]);

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const asAbs = (u) => (u ? (u.startsWith("http") ? u : `${backend}${u}`) : null);

  const openPurchaseTeaser = async (p) => {
    // Fetch the linked content to get preview + poster
    try {
      let previewUrl = p.preview_url;
      let posterUrl = p.product_image;
      let contentType = p.product_category === "video" ? "video" : "music";
      if (p.linked_content_id) {
        try {
          const { data } = await axios.get(`${API}/content/${p.linked_content_id}/preview`);
          previewUrl = data.preview_url || previewUrl;
          contentType = data.content_type || contentType;
        } catch { /* preview lookup optional — fall back to product-level fields */ }
      }
      setTeaser({
        open: true,
        item: {
          title: p.product_name,
          artistName: p.artist_name,
          previewUrl,
          posterUrl,
          contentType,
          productId: p.product_id,
          priceLabel: p.amount ? `$${Number(p.amount).toFixed(2)}` : null,
        },
        purchaseState: {
          owned: true,
          downloadsUsed: p.downloads_used,
          downloadsRemaining: p.downloads_remaining,
          maxDownloads: p.max_downloads,
        },
      });
    } catch (e) {
      toast.error("Couldn't open preview");
    }
  };

  const downloadTicketPdf = async (ticketId, ticketCode) => {
    try {
      const r = await axios.get(`${API}/tickets/${ticketId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `stardomfans-ticket-${ticketCode || ticketId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Couldn't download ticket");
    }
  };

  if (!user) return null;

  const purchases = wallet?.purchases || [];
  const tickets = wallet?.tickets || [];
  const subs = wallet?.subscriptions || [];
  const entries = wallet?.challenge_entries || [];
  const tips = wallet?.tips_sent || [];

  return (
    <div className="min-h-screen bg-black text-white pb-24">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur border-b border-[#1A2030] workspace-page-header">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-lg border border-[#1A2030] hover:bg-[#1A2030] flex items-center justify-center"
            aria-label="Back"
            data-testid="wallet-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Link to="/home" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="StardomFans Ltd." className="w-10 h-10 object-contain" />
            <span className="hidden sm:inline text-white font-bold">StardomFans Ltd.</span>
          </Link>
          <div className="ml-auto text-right">
            <p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">Your Library</p>
            <p className="text-white font-bold">Fan Wallet · Tokens</p>
          </div>
          <SiteNavigation scope="wallet" compact />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        <LibraryNavigation />
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Everything you own</h1>
          <p className="text-[#8A92A0] mt-2 text-sm sm:text-base">
            Welcome home to the StardomFans Family. (Operated by StardomFans Ltd.) Your tokens, songs, movies, tickets, memberships and challenge entries — all in one place.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-[#8A92A0]" data-testid="wallet-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading your library…
          </div>
        ) : (
          <div className="space-y-12">

            {/* 48-Hour New-Buyer Hold banner (renders only when active) */}
            <WalletHoldBanner token={token} />

            {/* Address confirmation gate — must be completed before top-up */}
            <AddressConfirmationCard token={token} onCredited={fetchWallet} />

            {/* Top-up card */}
            <CreditTopupCard token={token} onCredited={fetchWallet} />

            {/* Send a tip to the platform */}
            <div className="rounded-xl border border-[#EC008C]/25 bg-gradient-to-br from-[#EC008C]/[0.06] to-[#0A0E18] p-5 flex items-center gap-4" data-testid="wallet-tip-platform-card">
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">Say thanks</p>
                <h3 className="font-['Unbounded'] text-base font-bold text-white">Tip StardomFans Ltd.</h3>
                <p className="text-xs text-[#8A92A0]">Love the platform? Send 5 – 100 Tokens from your wallet. Zero fee.</p>
              </div>
              <TipButton token={token} artistName="StardomFans Ltd." onSent={fetchWallet} />
            </div>

            {/* Counts strip */}
            <section className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="wallet-counts">
              {[
                { icon: ShoppingBag, label: "Purchases", value: purchases.length, tint: "#32D583" },
                { icon: Ticket, label: "Tickets", value: tickets.length, tint: "#8DB87A" },
                { icon: Crown, label: "Memberships", value: subs.length, tint: "#EBB84C" },
                { icon: Music4, label: "Challenges", value: entries.length, tint: "#7AB8B8" },
                { icon: Coins, label: "Tips sent", value: tips.length, tint: "#B87AB8" },
              ].map((c) => (
                <div key={c.label} className="rounded-xl border border-[#1A2030] bg-[#0A0E18] p-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${c.tint}22`, color: c.tint }}>
                    <c.icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs text-[#8A92A0]">{c.label}</p>
                    <p className="text-lg font-bold">{c.value}</p>
                  </div>
                </div>
              ))}
            </section>

            {/* Purchases */}
            <section data-testid="wallet-purchases">
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="text-xl font-bold">Purchases</h2>
                <p className="text-xs text-[#4A5260]">{purchases.length} item{purchases.length === 1 ? "" : "s"}</p>
              </div>
              {purchases.length === 0 ? (
                <EmptyState
                  icon={ShoppingBag}
                  label="You haven't bought anything yet"
                  cta="Explore artists"
                  onClick={() => navigate("/fandom")}
                />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {purchases.map((p) => {
                    const isDigital = p.has_digital_file && p.download_url;
                    const isVideo = p.product_category === "video";
                    const CatIcon = isVideo ? Film : (p.product_category === "music" ? Music4 : Package);
                    const img = asAbs(p.product_image);
                    return (
                      <article
                        key={p.order_id}
                        className="rounded-xl overflow-hidden border border-[#1A2030] bg-[#0A0E18] hover:border-[#EC008C]/30 transition-colors"
                        data-testid={`wallet-purchase-${p.order_id}`}
                      >
                        <div className="aspect-video bg-black relative flex items-center justify-center">
                          {img ? (
                            <img src={img} alt={p.product_name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-[#EC008C]/20 via-[#0A0E18] to-black flex items-center justify-center">
                              <CatIcon className="w-10 h-10 text-[#EC008C]/60" />
                            </div>
                          )}
                          {isDigital && (
                            <span className="absolute top-2 left-2 px-2 py-0.5 rounded bg-black/70 text-[10px] font-bold uppercase tracking-widest text-[#8DB87A]">
                              Owned · {p.downloads_remaining}/{p.max_downloads} left
                            </span>
                          )}
                        </div>
                        <div className="p-3">
                          <h3 className="font-semibold truncate">{p.product_name}</h3>
                          <p className="text-xs text-[#8A92A0] truncate">{p.artist_name || "—"}</p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {isDigital && (
                              <button
                                onClick={() => openPurchaseTeaser(p)}
                                className="flex-1 min-w-[120px] h-9 rounded-full bg-[#EC008C] text-black text-sm font-bold hover:bg-[#FF3399] flex items-center justify-center gap-1.5"
                                data-testid={`wallet-download-${p.order_id}`}
                              >
                                <Download className="w-3.5 h-3.5" /> Download
                              </button>
                            )}
                            {p.preview_url && (
                              <button
                                onClick={() => openPurchaseTeaser(p)}
                                className="h-9 px-3 rounded-full border border-[#1A2030] hover:bg-[#1A2030] text-sm inline-flex items-center gap-1.5"
                                data-testid={`wallet-preview-${p.order_id}`}
                              >
                                <Play className="w-3.5 h-3.5" /> Teaser
                              </button>
                            )}
                            {p.requires_shipping && (
                              <span className="h-9 px-3 rounded-full border border-[#1A2030] text-xs inline-flex items-center gap-1.5 text-[#8A92A0]">
                                {p.shipping_status || "Shipped"}
                              </span>
                            )}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Tickets */}
            <section data-testid="wallet-tickets">
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="text-xl font-bold">Live tickets</h2>
                <p className="text-xs text-[#4A5260]">{tickets.length} ticket{tickets.length === 1 ? "" : "s"}</p>
              </div>
              {tickets.length === 0 ? (
                <EmptyState icon={Ticket} label="No live tickets yet" cta="Browse events" onClick={() => navigate("/fandom")} />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {tickets.map((t) => (
                    <article
                      key={t.ticket_id}
                      className="rounded-xl border border-[#1A2030] bg-[#0A0E18] p-4 flex items-center gap-4"
                      data-testid={`wallet-ticket-${t.ticket_id}`}
                    >
                      <div className="w-14 h-14 rounded-lg bg-[#EC008C]/15 border border-[#EC008C]/30 flex items-center justify-center flex-shrink-0">
                        <Ticket className="w-5 h-5 text-[#EC008C]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold truncate">{t.event_title || "Event"}</h3>
                        <p className="text-xs text-[#8A92A0] truncate">
                          {t.artist_name} · {t.event_date || "TBA"} {t.event_time || ""}
                        </p>
                        <p className="text-xs text-[#4A5260] truncate">{t.venue} {t.location ? `— ${t.location}` : ""}</p>
                        <div className="mt-2 flex gap-2 flex-wrap">
                          <button
                            onClick={() => downloadTicketPdf(t.ticket_id, t.ticket_code)}
                            className="h-8 px-3 rounded-full bg-[#EC008C] text-black text-xs font-bold hover:bg-[#FF3399] flex items-center gap-1.5"
                            data-testid={`wallet-ticket-pdf-${t.ticket_id}`}
                          >
                            <Download className="w-3 h-3" /> PDF
                          </button>
                          <span className={`h-8 px-3 rounded-full text-xs inline-flex items-center border ${t.status === "scanned" ? "border-[#4A5260] text-[#4A5260]" : "border-[#8DB87A]/40 text-[#8DB87A]"}`}>
                            {(t.status || "valid").toUpperCase()}
                          </span>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            {/* Memberships */}
            {subs.length > 0 && (
              <section data-testid="wallet-subscriptions">
                <h2 className="text-xl font-bold mb-4">Memberships</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {subs.map((s) => (
                    <article key={s.subscription_id} className="rounded-xl border border-[#1A2030] bg-[#0A0E18] p-4">
                      <p className="text-xs text-[#8A92A0]">{s.artist_name}</p>
                      <p className="text-lg font-bold">{s.tier || "Membership"}</p>
                      <p className="text-xs text-[#4A5260] mt-1">
                        {s.amount ? `$${Number(s.amount).toFixed(2)} / month` : ""} · {s.status}
                      </p>
                    </article>
                  ))}
                </div>
              </section>
            )}

            {/* Challenge entries */}
            {entries.length > 0 && (
              <section data-testid="wallet-challenges">
                <h2 className="text-xl font-bold mb-4">Challenge entries</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {entries.map((e, i) => (
                    <article key={e.entry_id || i} className="rounded-xl border border-[#1A2030] bg-[#0A0E18] p-4">
                      <p className="text-sm truncate">{e.title || e.challenge_title || "Challenge entry"}</p>
                      <p className="text-xs text-[#4A5260] mt-1">{e.submitted_at || ""}</p>
                    </article>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>

      <TeaserModal
        open={teaser.open}
        onClose={() => setTeaser({ open: false, item: null, purchaseState: null })}
        item={teaser.item}
        purchaseState={teaser.purchaseState}
        token={token}
        onDownloaded={fetchWallet}
      />
    </div>
  );
};

const CreditTopupCard = ({ token, onCredited }) => {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [balance, setBalance] = useState(null);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/credits/balance`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setBalance(data);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Auto-confirm if the buyer came back with ?topup=ok&session_id=…
  useEffect(() => {
    const url = new URL(window.location.href);
    const sid = url.searchParams.get("session_id");
    const status = url.searchParams.get("topup");
    if (status === "ok" && sid && token) {
      (async () => {
        try {
          const { data } = await axios.post(`${API}/credits/topup/confirm/${sid}`, {}, { headers: { Authorization: `Bearer ${token}` } });
          if (data.status === "completed") {
            toast.success(`Credited ${(data.amount_cents / 100).toFixed(0)} Tokens — new balance ${(data.balance_cents / 100).toFixed(0)} Tokens`);
            onCredited && onCredited();
            // scrub the query params so a refresh doesn't re-fire
            url.searchParams.delete("session_id");
            url.searchParams.delete("topup");
            window.history.replaceState({}, "", url.toString());
          }
        } catch { /* ignore */ }
      })();
    }
  }, [token, onCredited]);

  const submit = async (e) => {
    e.preventDefault();
    const n = parseFloat(amount);
    if (!n || n < 1) return toast.error("Enter an amount of at least $1");
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/credits/topup`,
        {
          amount_cents: Math.round(n * 100),
          success_url: `${window.location.origin}/wallet?topup=ok&session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/wallet?topup=cancel`,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPending({ ...data });
      // Wallet top-ups run through Stripe Checkout. Buyers outside the
      // 8 supported markets get a clear 409 handled by the catch block.
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Top-up failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-[#1A2030] bg-gradient-to-br from-[#EC008C]/10 via-[#0A0E18] to-[#0A0E18] p-5 sm:p-6" data-testid="credit-topup-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#EC008C]/15 border border-[#EC008C]/30 flex items-center justify-center flex-shrink-0">
          <Coins className="w-5 h-5 text-[#EC008C]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">Token balance</p>
          <p className="text-2xl font-black text-white">{balance?.balance_display || "0 Tokens"} <span className="text-sm text-[#8A92A0] font-normal">(1 Token = $1 USD)</span></p>
          <p className="text-xs text-[#8A92A0] mt-1">Top up in seconds — spend on any drop, ticket, or membership on StardomFans Ltd.</p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-2">
        <div className="flex items-center gap-2 bg-black/40 border border-[#1A2030] rounded-full px-3 flex-1">
          <Coins className="w-4 h-4 text-[#EC008C]" />
          <input
            type="number"
            min="1"
            step="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="flex-1 bg-transparent outline-none text-white py-2"
            placeholder="10"
            data-testid="topup-amount-input"
          />
          <span className="text-xs text-[#8A92A0]">Tokens</span>
        </div>
        {[5, 10, 25, 50].map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setAmount(String(v))}
            className="h-10 px-4 rounded-full border border-[#1A2030] text-white text-sm hover:bg-[#1A2030]"
            data-testid={`topup-preset-${v}`}
          >
            {v}
          </button>
        ))}
        <button
          type="submit"
          disabled={busy}
          className="h-10 px-5 rounded-full bg-[#EC008C] text-black font-bold hover:bg-[#FF3399] disabled:opacity-60"
          data-testid="topup-submit-btn"
        >
          {busy ? "Redirecting…" : "Confirm & Top Up"}
        </button>
      </form>

      {pending && !pending.direct_deposit && (
        <p className="mt-3 text-[11px] text-[#8A92A0]" data-testid="topup-pending-line">
          Session: <span className="font-mono">{pending.session_id?.slice(0, 24)}…</span>
          <span> · no fees, no tax at top-up · 1 Token = $1 USD</span>
        </p>
      )}
    </section>
  );
};

const EmptyState = ({ icon: Icon, label, cta, onClick }) => (
  <div className="rounded-xl border border-dashed border-[#1A2030] bg-[#0A0E18]/60 p-8 text-center">
    <Icon className="w-8 h-8 text-[#4A5260] mx-auto mb-3" />
    <p className="text-[#8A92A0] mb-4">{label}</p>
    {cta && (
      <button
        onClick={onClick}
        className="h-10 px-5 rounded-full bg-[#EC008C] text-black font-bold hover:bg-[#FF3399] text-sm"
      >
        {cta}
      </button>
    )}
  </div>
);

// (Auth is enforced by <ProtectedRoute> in App.js; no local Navigate needed.)

export default Wallet;
